import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { applyTheme } from "@/theme";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/panel/AuthPage";
import ProtectedRoute from "@/panel/guards";
import PanelLayout from "@/panel/PanelLayout";
import DashboardPage from "@/panel/DashboardPage";
import NewOrderPage from "@/panel/NewOrderPage";
import ServicesPage from "@/panel/ServicesPage";
import OrdersPage from "@/panel/OrdersPage";
import TransactionsPage from "@/panel/TransactionsPage";
import AddFundsPage from "@/panel/AddFundsPage";
import TicketsPage from "@/panel/TicketsPage";
import NotificationsPage from "@/panel/NotificationsPage";
import ProfilePage from "@/panel/ProfilePage";
import AdminLogin from "@/admin/AdminLogin";
import AdminGuard from "@/admin/AdminGuard";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminUsers from "@/admin/AdminUsers";
import AdminOrders from "@/admin/AdminOrders";
import AdminServices from "@/admin/AdminServices";
import AdminCategories from "@/admin/AdminCategories";
import AdminSupport from "@/admin/AdminSupport";
import AdminMaintenance from "@/admin/AdminMaintenance";
import AdminAudit from "@/admin/AdminAudit";
import AdminContent from "@/admin/AdminContent";
import AdminBroadcast from "@/admin/AdminBroadcast";
import AdminPayments from "@/admin/AdminPayments";
import AdminTransactions from "@/admin/AdminTransactions";
import AdminInvoices from "@/admin/AdminInvoices";
import AdminSendMail from "@/admin/AdminSendMail";
import AdminTransfer from "@/admin/AdminTransfer";
import AdminStatements from "@/admin/AdminStatements";
import AdminMailLog from "@/admin/AdminMailLog";
import AdminRevenue from "@/admin/AdminRevenue";
import AdminRefunds from "@/admin/AdminRefunds";
import AdminOffers from "@/admin/AdminOffers";
import AdminProductProfit from "@/admin/AdminProductProfit";
import AdminAnalyzer from "@/admin/AdminAnalyzer";
import AdminSettings from "@/admin/AdminSettings";
import TermsPage from "@/pages/TermsPage";
import ResetPage from "@/pages/ResetPage";
import VerifyStatementPage from "@/pages/VerifyStatementPage";

function App() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && applyTheme(d.theme || "obsidian-gold"))
      .catch(() => {});
  }, []);
  return (
    <BrowserRouter>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "rgb(var(--panel))",
            border: "1px solid rgb(var(--gold) / 0.25)",
            color: "#F7F7F8",
          },
        }}
      />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/panel/login" element={<AuthPage mode="login" />} />
        <Route path="/panel/signup" element={<AuthPage mode="signup" />} />
        <Route path="/panel/reset" element={<ResetPage />} />
        <Route path="/verify/statement/:statementId" element={<VerifyStatementPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/panel" element={<PanelLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="new-order" element={<NewOrderPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="add-funds" element={<AddFundsPage />} />
            <Route path="support" element={<TicketsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route element={<AdminGuard />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="invoices" element={<AdminInvoices />} />
            <Route path="send-mail" element={<AdminSendMail />} />
            <Route path="transfer" element={<AdminTransfer />} />
            <Route path="statements" element={<AdminStatements />} />
            <Route path="product-profit" element={<AdminProductProfit />} />
            <Route path="analyzer" element={<AdminAnalyzer />} />
            <Route path="mail-log" element={<AdminMailLog />} />
            <Route path="offers" element={<AdminOffers />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="refunds" element={<AdminRefunds />} />
            <Route path="broadcast" element={<AdminBroadcast />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
